import { useEffect, useState } from "react";
import { capMap } from "@shared/boundedMap";

/**
 * What a logo looks like once its padding is taken away.
 *
 * Builders upload their logo as a square tile — a wordmark in the middle of a
 * field of brand colour, or of transparency — and the card used to draw that
 * whole square in a 52px disc. The name inside it came out about 8px tall.
 * The project card now draws the MARK alone on a plate of the tile's own
 * colour (components/ui/logo-plate.tsx), which is what this works out: the box the mark
 * occupies, and the colour it was sitting on.
 *
 * It is worked out in the browser, from the file the card already loads, not
 * stored at upload: every logo already on the platform gets it at once, with
 * no backfill, and the same module serves PropPay LX (client/src/lib/logo-plate.ts).
 */
export interface LogoAnalysis {
  /** The tile colour around the mark, `#rrggbb`; null when the ground is transparent. */
  ground: string | null;
  /** The mark's box inside the image, as fractions of its width and height. */
  box: { x: number; y: number; w: number; h: number };
  /** The image's own width ÷ height, so the crop can be drawn before it loads. */
  ratio: number;
}

const FULL = { x: 0, y: 0, w: 1, h: 1 };
/** How far a pixel must sit from the ground colour (sum of |ΔR|+|ΔG|+|ΔB|) to count as mark. */
const INK_DISTANCE = 60;
/** Corners further apart than this are not one ground colour: the logo runs to its edges. */
const GROUND_SPREAD = 36;

const hex = (r: number, g: number, b: number) =>
  "#" + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");

function luminance(r: number, g: number, b: number): number {
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/**
 * The mark's box and ground, from an image's RGBA pixels.
 *
 * Pure, so it is tested without a canvas. The ground is read from the four
 * corners: transparent corners mean a transparent logo (the mark is whatever
 * is opaque); four corners of one colour mean a tile (the mark is whatever
 * differs from it); corners that disagree mean the artwork runs to the edge,
 * and nothing is cropped.
 */
export function analyseLogo(px: Uint8ClampedArray | Uint8Array, w: number, h: number): LogoAnalysis {
  const ratio = w / h;
  const at = (x: number, y: number) => {
    const i = (y * w + x) * 4;
    return [px[i], px[i + 1], px[i + 2], px[i + 3]] as const;
  };
  const corners = [at(0, 0), at(w - 1, 0), at(0, h - 1), at(w - 1, h - 1)];
  const transparent = corners.some((c) => c[3] < 128);

  let ground: [number, number, number] | null = null;
  if (!transparent) {
    // The ground is what at least three corners agree on: a mark can run into
    // one corner (Founders Walk's shield fills its file's top-left) and the
    // other three still say what it sits on.
    const near = (c: readonly number[], d: readonly number[]) => Math.max(...[0, 1, 2].map((k) => Math.abs(c[k] - d[k]))) <= GROUND_SPREAD;
    const agreeing = corners.filter((c) => corners.filter((d) => near(c, d)).length >= 3);
    if (agreeing.length < 3) {
      // Artwork to the edges: show it whole, on no plate colour of its own.
      return { ground: null, box: FULL, ratio };
    }
    ground = [0, 1, 2].map((k) => agreeing.reduce((sum, c) => sum + c[k], 0) / agreeing.length) as [number, number, number];
  }

  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const a = px[i + 3];
      let isInk: boolean;
      if (!ground) {
        isInk = a > 40;
      } else {
        // Blend onto the ground first, so a soft anti-aliased edge counts by how visible it is.
        const t = a / 255;
        const r = px[i] * t + ground[0] * (1 - t);
        const g = px[i + 1] * t + ground[1] * (1 - t);
        const b = px[i + 2] * t + ground[2] * (1 - t);
        isInk = Math.abs(r - ground[0]) + Math.abs(g - ground[1]) + Math.abs(b - ground[2]) > INK_DISTANCE;
      }
      if (!isInk) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  const groundHex = ground ? hex(ground[0], ground[1], ground[2]) : null;
  if (x1 < 0) return { ground: groundHex, box: FULL, ratio };
  return {
    ground: groundHex,
    box: { x: x0 / w, y: y0 / h, w: (x1 - x0 + 1) / w, h: (y1 - y0 + 1) / h },
    ratio,
  };
}

/**
 * Whether a logo was uploaded as a tile — a mark on a field of its own colour —
 * and is to be shown whole, as that tile, rather than on the white plate
 * (Andrew, 2026-10-23: "white plate in both themes, a tile logo shown whole as
 * its own tile"). A near-white ground is not a tile: that logo was drawn for
 * white, and goes on the white plate like a transparent one.
 */
export const isTile = (a: LogoAnalysis): a is LogoAnalysis & { ground: string } =>
  !!a.ground && luminance(...([1, 3, 5].map((k) => parseInt(a.ground!.slice(k, k + 2), 16)) as [number, number, number])) < 0.8;

/** CIE L* (0–100) from relative luminance. */
const lightness = (y: number) => (y > 0.008856 ? 116 * Math.cbrt(y) - 16 : 903.3 * y);

/**
 * A builder's mark in one tone: how strongly each pixel belongs to the mark,
 * 0–255, to be filled with the card's muted text colour (Andrew, 2026-10-23:
 * builder logos greyscale, on no plate, working on light and dark cards, so
 * they do not compete with the project's own logo).
 *
 * On transparency, darker ink is stronger: OPUS's crimson box stays solid and
 * the white ring inside it becomes a hole, as it is drawn; York Trafalgar's
 * navy Y and grey T come out as two shades. On a tile, distance from the tile
 * colour is strength: Mattamy's white lettering stays, its navy square goes.
 * Either way the mark's strongest tone is scaled to full, so a pale logo is as
 * present as a dark one. A white mark on transparency has no dark ink to read,
 * so its shape is its alpha.
 */
export function toneMask(px: Uint8ClampedArray | Uint8Array, w: number, h: number, ground: string | null): Uint8ClampedArray {
  const g = ground ? [1, 3, 5].map((k) => parseInt(ground.slice(k, k + 2), 16)) : null;
  const weight = new Float32Array(w * h);
  for (let j = 0; j < w * h; j++) {
    const i = j * 4;
    const t = px[i + 3] / 255;
    if (g) {
      const dr = (px[i] - g[0]) * t, dg = (px[i + 1] - g[1]) * t, db = (px[i + 2] - g[2]) * t;
      weight[j] = Math.sqrt(dr * dr + dg * dg + db * db) / 441.7;
    } else {
      weight[j] = t * (1 - lightness(luminance(px[i], px[i + 1], px[i + 2])) / 100);
    }
  }
  // The 98th percentile of the mark's pixels, so one stray dark pixel cannot set the scale.
  const marked = Array.from(weight).filter((v) => v > 0.02).sort((a, b) => a - b);
  let top = marked.length ? marked[Math.floor((marked.length - 1) * 0.98)] : 0;
  if (!g && top < 0.05) {
    for (let j = 0; j < w * h; j++) weight[j] = px[j * 4 + 3] / 255;
    top = 1;
  }
  const out = new Uint8ClampedArray(w * h);
  if (top > 0) for (let j = 0; j < w * h; j++) out[j] = Math.round((weight[j] / top) * 255);
  return out;
}

/**
 * The size to draw a mark at, and how to place its image so only the mark shows.
 *
 * The plate is a window `width × height` with overflow hidden; the image
 * inside it is drawn larger and shifted so the mark's box fills the window.
 */
export function markLayout(a: LogoAnalysis, maxW: number, maxH: number) {
  const aspect = (a.box.w / a.box.h) * a.ratio;
  const height = Math.min(maxH, maxW / aspect);
  const width = height * aspect;
  const imgW = width / a.box.w;
  const imgH = height / a.box.h;
  return {
    width: Math.round(width),
    height: Math.round(height),
    img: { width: imgW, height: imgH, left: -a.box.x * imgW, top: -a.box.y * imgH },
  };
}

/* ---- in the browser ---------------------------------------------------- */

/** Long side of the copy the analysis reads. The box is a fraction, so it holds at any size. */
const SAMPLE_EDGE = 160;
const STORE_KEY = "sf.logo-plate.v4"; // v4: no ink reading; the plate no longer depends on it
const memory = new Map<string, LogoAnalysis | "failed">();
/** As many logos as the device keeps (writeStored); past it the oldest are read again. */
const MEMORY_KEPT = 400;
const pending = new Map<string, Promise<LogoAnalysis | "failed">>();

/*
 * Logo files never change in place (a new upload is a new path), so a result
 * is good for as long as the URL is. Kept per device in localStorage so a
 * reload draws the plate at once instead of after the image decodes — wrapped,
 * because a private window or blocked site data throws.
 */
function readStored(src: string): LogoAnalysis | undefined {
  try {
    const all = JSON.parse(localStorage.getItem(STORE_KEY) ?? "{}") as Record<string, LogoAnalysis>;
    return all[src];
  } catch {
    return undefined;
  }
}
function writeStored(src: string, a: LogoAnalysis) {
  try {
    const all = JSON.parse(localStorage.getItem(STORE_KEY) ?? "{}") as Record<string, LogoAnalysis>;
    all[src] = a;
    const keys = Object.keys(all);
    // A few hundred logos at most; keep the newest 400 so it cannot grow without end.
    for (const k of keys.slice(0, Math.max(0, keys.length - 400))) delete all[k];
    localStorage.setItem(STORE_KEY, JSON.stringify(all));
  } catch {
    /* storage unavailable: the result still lives in memory for this visit */
  }
}

export function cachedLogoAnalysis(src: string | null | undefined): LogoAnalysis | "failed" | undefined {
  if (!src) return undefined;
  const hit = memory.get(src);
  if (hit) return hit;
  const stored = readStored(src);
  if (stored) {
    memory.set(src, stored);
    capMap(memory, MEMORY_KEPT);
  }
  return stored;
}

function analyseUrl(src: string): Promise<LogoAnalysis | "failed"> {
  const inflight = pending.get(src);
  if (inflight) return inflight;
  const run = new Promise<LogoAnalysis | "failed">((resolve) => {
    const img = new Image();
    // Reading pixels needs a CORS response; the plate's own <img> asks the same
    // way, so the two share one cache entry instead of a tainted one.
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => {
      try {
        const scale = Math.min(1, SAMPLE_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return resolve("failed");
        ctx.drawImage(img, 0, 0, w, h);
        const a = analyseLogo(ctx.getImageData(0, 0, w, h).data, w, h);
        // The sample's ratio is rounded; the image's own is exact.
        a.ratio = img.naturalWidth / img.naturalHeight;
        writeStored(src, a);
        resolve(a);
      } catch {
        // A canvas the browser will not let us read (no CORS on that host).
        resolve("failed");
      }
    };
    img.onerror = () => resolve("failed");
    img.src = src;
  }).then((r) => {
    memory.set(src, r);
    capMap(memory, MEMORY_KEPT);
    pending.delete(src);
    return r;
  });
  pending.set(src, run);
  return run;
}

/**
 * The analysis for a logo URL: at once when this device has seen the logo,
 * otherwise undefined until it has been read. "failed" means draw the logo
 * whole — the plate's fallback.
 */
export function useLogoAnalysis(src: string | null | undefined): LogoAnalysis | "failed" | undefined {
  // Keyed by the URL it is for: a card first drawn before its logo was known
  // (the builder list arrives after the projects) must read the device's copy
  // in the render where the URL arrives, not one effect later — that frame
  // drew the mark blank on every launch.
  const [state, setState] = useState(() => ({ src, result: cachedLogoAnalysis(src) }));
  useEffect(() => {
    const now = cachedLogoAnalysis(src);
    setState({ src, result: now });
    if (!src || now) return;
    let live = true;
    analyseUrl(src).then((r) => live && setState({ src, result: r }));
    return () => {
      live = false;
    };
  }, [src]);
  return state.src === src ? state.result : cachedLogoAnalysis(src);
}

/*
 * The one-tone mask for a builder's mark, drawn at the size the card shows it
 * (three times over, for a sharp edge on any screen), as a PNG data URL.
 *
 * KEPT ON THE DEVICE (Andrew, 2026-10-27: "the project cards loading fresh
 * every time"). It used to live in memory only, as "cheap to redraw" — but a
 * redraw is: load the logo, decode it, read its pixels, encode a PNG, and the
 * mark is blank until all of that has happened. Every sign-in and sign-out is
 * a full page load, so every card's builder mark drew late on every launch,
 * and the hook started each mount `undefined` even when the mask was already
 * in memory, so returning to the picker blanked them for a frame too. A mask
 * is a one-colour PNG a few KB long; the newest MASKS_KEPT fit comfortably in
 * localStorage, and the key includes the URL (a changed logo is a new URL) and
 * the drawn height.
 */
const MASK_STORE_KEY = "sf.builder-mask.v1";
const MASKS_KEPT = 60;
const masks = new Map<string, Promise<string | null>>();
const drawnMasks = new Map<string, string | null>();
/** Each holds a PNG data URL; kept to a few times what the device stores. */
const MASKS_IN_MEMORY = MASKS_KEPT * 3;

/** Parsed once per page load: the hook may ask on every render of every card. */
let storedMasks: Record<string, string> | null = null;

function readStoredMasks(): Record<string, string> {
  if (storedMasks) return storedMasks;
  try {
    storedMasks = JSON.parse(localStorage.getItem(MASK_STORE_KEY) ?? "{}") as Record<string, string>;
  } catch {
    storedMasks = {};
  }
  return storedMasks;
}

function writeStoredMask(key: string, url: string) {
  const all = readStoredMasks();
  delete all[key];
  all[key] = url; // re-inserted last, so the newest survive the trim
  const keys = Object.keys(all);
  for (const k of keys.slice(0, Math.max(0, keys.length - MASKS_KEPT))) delete all[k];
  try {
    localStorage.setItem(MASK_STORE_KEY, JSON.stringify(all));
  } catch {
    /* storage full or unavailable: the mask still lives in memory for this visit */
  }
}

/** The mask for this logo at this height, if this device has drawn it before. */
export function cachedToneMask(src: string | null | undefined, height: number): string | null | undefined {
  if (!src || height <= 0) return undefined;
  const key = `${src}|${height}`;
  if (drawnMasks.has(key)) return drawnMasks.get(key);
  const stored = readStoredMasks()[key];
  if (stored) {
    drawnMasks.set(key, stored);
    capMap(drawnMasks, MASKS_IN_MEMORY);
  }
  return stored;
}

function toneMaskUrl(src: string, a: LogoAnalysis, height: number): Promise<string | null> {
  const key = `${src}|${height}`;
  const hit = masks.get(key);
  if (hit) return hit;
  const stored = cachedToneMask(src, height);
  if (stored !== undefined) {
    const done = Promise.resolve(stored);
    masks.set(key, done);
    capMap(masks, MASKS_IN_MEMORY);
    return done;
  }
  const run = new Promise<string | null>((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => {
      try {
        const sx = a.box.x * img.naturalWidth, sy = a.box.y * img.naturalHeight;
        const sw = a.box.w * img.naturalWidth, sh = a.box.h * img.naturalHeight;
        const ch = Math.max(1, Math.round(height * 3));
        const cw = Math.max(1, Math.round((ch * sw) / sh));
        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return resolve(null);
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
        const data = ctx.getImageData(0, 0, cw, ch);
        const alpha = toneMask(data.data, cw, ch, a.ground);
        for (let j = 0; j < alpha.length; j++) {
          data.data[j * 4] = data.data[j * 4 + 1] = data.data[j * 4 + 2] = 0;
          data.data[j * 4 + 3] = alpha[j];
        }
        ctx.putImageData(data, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  }).then((url) => {
    drawnMasks.set(key, url);
    capMap(drawnMasks, MASKS_IN_MEMORY);
    if (url) writeStoredMask(key, url);
    return url;
  });
  masks.set(key, run);
  capMap(masks, MASKS_IN_MEMORY);
  return run;
}

/**
 * The builder mark's one-tone mask, once drawn; null if it cannot be. Starts
 * from the device's copy, so a mask drawn on any earlier visit is there in the
 * first frame.
 */
export function useToneMask(src: string | null | undefined, a: LogoAnalysis | null, height: number): string | null | undefined {
  // Keyed like useLogoAnalysis, for the same reason: a URL or height that
  // arrives after the first render is read from the device in that render.
  const key = `${src}|${height}`;
  const [state, setState] = useState<{ key: string; url: string | null | undefined }>(() => ({ key, url: cachedToneMask(src, height) }));
  useEffect(() => {
    const known = cachedToneMask(src, height);
    setState({ key, url: known });
    if (known !== undefined || !src || !a || height <= 0) return;
    let live = true;
    toneMaskUrl(src, a, height).then((u) => live && setState({ key, url: u }));
    return () => {
      live = false;
    };
  }, [src, a, height, key]);
  return state.key === key ? state.url : cachedToneMask(src, height);
}
